import {useContext, useEffect, useState} from "react"
import {useParams, Link, useNavigate} from "react-router-dom"
import {
    Typography,
    Button,
    CircularProgress,
    Grid2, Accordion, AccordionSummary, AccordionDetails, IconButton, Tooltip,
} from "@mui/material"
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import ImageGallery from "../components/ImageGallery.tsx"
import AuthContext from "../contexts/AuthContext.tsx"
import ProductContext from "../contexts/ProductContext.tsx"
import ErrorPage from "../components/ErrorPage.tsx"
import {useTranslation} from "react-i18next";
import {SupportedLanguages} from "../models/Languages.ts";
import CartContext from "../contexts/CartContext.tsx";
import OrderContext from "../contexts/OrderContext.tsx";
import SaleContext from "../contexts/SaleContext.tsx";
import {IProduct} from "../models/IProduct.ts";

const ProductPage = () => {
    const {id} = useParams<{ id: string }>()
    const {sales} = useContext(SaleContext)
    const {isAdmin} = useContext(AuthContext)
    const {t, i18n} = useTranslation()
    const navigate = useNavigate()
    const currentLanguage: SupportedLanguages = i18n.language as SupportedLanguages;
    const {selectedCurrency, rates} = useContext(OrderContext)
    const {useFetchProduct, deleteImageMutation, deleteProductMutation, deleteProductError} = useContext(ProductContext)
    const {addCartItemMutation} = useContext(CartContext)
    const {data: fetchedProduct, error: errorFetchingProduct} = useFetchProduct(id)
    const userLocale = navigator.language || 'en-US';
    const [loading, setLoading] = useState<boolean>(false);
    const [product, setProduct] = useState<IProduct>();

    const clearTime = (date: Date) => {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        return d;
    };

    const handleDelete = async () => {
        const confirmDelete = window.confirm('Sicher, dass du das löschen willst, Nina?');
        if (!confirmDelete) return;
        setLoading(true)
        product!.images.map(async (image: string) => {
            await deleteImageMutation.mutateAsync(image)
        })
        await deleteProductMutation.mutateAsync(product!._id)
        setLoading(false)
        if (!deleteProductError) {
            navigate('/')
        }
    };

    const handleAddToCart = async (productId: string | undefined) => {
        addCartItemMutation.mutate(productId)
    };

    useEffect(() => {
        if (!fetchedProduct) return;

        const nowDay = clearTime(new Date());

        let salePrice: number | undefined = undefined;

        sales?.forEach((sale) => {
            const saleStartDay = clearTime(new Date(sale.from));
            const saleEndDay = clearTime(new Date(sale.to));

            const saleIsActive = saleStartDay <= nowDay && saleEndDay >= nowDay;

            if (saleIsActive && sale.items.find((item) => item === fetchedProduct._id)) {
                salePrice = fetchedProduct.price * (1 - sale.discount);
            }
        });

        setProduct({
            ...fetchedProduct,
            salePrice: salePrice,
        });

    }, [sales, fetchedProduct]);

    if (!product) return <CircularProgress sx={{margin: "auto", display: "block"}}/>

    if (errorFetchingProduct)
        return (
            <ErrorPage/>
        );

    return (
        <Grid2 container sx={{
            pt: 3,
            pl: 3,
            pb: 3,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
        }}>
            <Grid2 container>
                <Grid2
                    size={{xs: 12, md: 6}}
                    mr={3}
                    mb={2}
                >
                    <ImageGallery images={product.images}/>
                </Grid2>
                <Grid2
                    size={{xs: 12, md: 5}}
                    mr={3}
                >
                    <Typography variant="h5" gutterBottom>
                        {product.name[currentLanguage]}
                    </Typography>

                    {product.salePrice !== undefined ? (
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                            <Typography
                                variant="body1"
                                color="text.secondary"
                                sx={{textDecoration: 'line-through'}}
                            >
                                {new Intl.NumberFormat(userLocale, {
                                    style: 'currency',
                                    currency: selectedCurrency,
                                }).format(product.price * rates[selectedCurrency])}
                            </Typography>
                            <Typography
                                variant="body1"
                                color="error"
                                fontWeight="bold"
                            >
                                {new Intl.NumberFormat(userLocale, {
                                    style: 'currency',
                                    currency: selectedCurrency,
                                }).format(product.salePrice * rates[selectedCurrency])}
                            </Typography>
                        </div>
                    ) : (
                        <Typography variant="body1" color="text.secondary">
                            {new Intl.NumberFormat(userLocale, {
                                style: 'currency',
                                currency: selectedCurrency,
                            }).format(product.price * rates[selectedCurrency])}
                        </Typography>
                    )}

                    <Typography
                        variant="body1"
                        sx={{whiteSpace: 'pre-line', pb: 1}}
                    >
                        {product.description[currentLanguage]}
                    </Typography>
                    <Grid2 sx={{mt: 1, mb: 1}}>
                        {product.amount === 0 ? (
                            <IconButton color="secondary" disabled>
                                <AddShoppingCartIcon/>
                            </IconButton>
                        ) : (
                            <Tooltip title={t("CART_BUTTON_TEXT")}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => handleAddToCart(product._id)}
                                >
                                    {t("CART_BUTTON_TEXT")}
                                </Button>
                            </Tooltip>
                        )}
                    </Grid2>
                    <Accordion
                        sx={{
                            boxShadow: 'none',
                            border: 'none',
                            '&::before': {
                                display: 'none',
                            },
                        }}
                    >
                        <AccordionSummary
                            sx={{
                                paddingLeft: 0,
                                paddingX: 0,
                            }}
                            expandIcon={<ExpandMoreIcon/>}
                        >
                            <Typography variant="h6">{t("MATERIALS")}</Typography>
                        </AccordionSummary>
                        <AccordionDetails
                            sx={{
                                paddingLeft: 1,
                            }}
                        >
                            <Typography sx={{whiteSpace: 'pre-line'}}>
                                {product.materials[currentLanguage]}
                            </Typography>
                        </AccordionDetails>
                    </Accordion>
                    <Accordion
                        sx={{
                            boxShadow: 'none',
                            border: 'none',
                            '&::before': {
                                display: 'none',
                            },
                        }}
                    >
                        <AccordionSummary
                            sx={{
                                paddingLeft: 0,
                                paddingX: 0,
                            }}
                            expandIcon={<ExpandMoreIcon/>}
                        >
                            <Typography variant="h6">{t("CARE_INSTRUCTIONS")}</Typography>
                        </AccordionSummary>
                        <AccordionDetails
                            sx={{
                                paddingLeft: 1,
                            }}
                        >
                            <Typography sx={{whiteSpace: 'pre-line'}}>
                                {product.careInstructions[currentLanguage]}
                            </Typography>
                        </AccordionDetails>
                    </Accordion>
                    <Accordion
                        sx={{
                            boxShadow: 'none',
                            border: 'none',
                            '&::before': {
                                display: 'none',
                            },
                        }}
                    >
                        <AccordionSummary
                            sx={{
                                paddingLeft: 0,
                                paddingX: 0,
                            }}
                            expandIcon={<ExpandMoreIcon/>}
                        >
                            <Typography variant="h6">{t("GENERAL_INFORMATION")}</Typography>
                        </AccordionSummary>
                        <AccordionDetails
                            sx={{
                                paddingLeft: 1,
                            }}
                        >
                            <Typography sx={{whiteSpace: 'pre-line'}}>
                                {t("GENERAL_INFORMATION_TEXT")}
                            </Typography>
                        </AccordionDetails>
                    </Accordion>
                    <Accordion
                        sx={{
                            boxShadow: 'none',
                            border: 'none',
                            '&::before': {
                                display: 'none',
                            },
                        }}
                    >
                        <AccordionSummary
                            sx={{
                                paddingLeft: 0,
                                paddingX: 0,
                            }}
                            expandIcon={<ExpandMoreIcon/>}
                        >
                            <Typography variant="h6">{t("MANUFACTURES_INFORMATION")}</Typography>
                        </AccordionSummary>
                        <AccordionDetails
                            sx={{
                                paddingLeft: 1,
                            }}
                        >
                            <Typography sx={{whiteSpace: 'pre-line'}}>
                                {t("MANUFACTURES_INFORMATION_TEXT")}
                            </Typography>
                        </AccordionDetails>
                    </Accordion>

                    {isAdmin && (
                        <Grid2 container>
                            <Grid2 size={12}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    component={Link}
                                    to={`/edit-product/${product._id}`}
                                    sx={{mt: 3}}
                                >
                                    {t('EDIT_PRODUCT_BUTTON_TEXT')}
                                </Button>
                            </Grid2>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={handleDelete}
                                disabled={loading}
                                sx={{mt: 3}}
                            >
                                {
                                    !loading ? t('DELETE_PRODUCT_BUTTON_TEXT') : <CircularProgress/>
                                }

                            </Button>
                        </Grid2>
                    )}
                </Grid2>
            </Grid2>
        </Grid2>
    )
}

export default ProductPage
